import { createContext, type FC, type PropsWithChildren, useContext, useEffect, useMemo } from "react";

import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import { currentUserQueryDocument } from "@/domain/user/hooks/api/user.gql";
import { identifyFaroUser } from "@/services/faro.service";
import { posthogService } from "@/services/posthog.service";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { CurrentUser } from "../models/current-user-model";

export type CurrentUserContext = {
  currentUser: CurrentUser;
  isSuperUser: boolean;
};

const CurrentUserContext = createContext<CurrentUserContext | null>(null);

export interface CurrentUserContextProviderProps extends PropsWithChildren {}

export const CurrentUserContextProvider: FC<CurrentUserContextProviderProps> = ({ children }) => {
  const queryResult = useQuery({
    queryKey: queryKeys.users.current(),
    queryFn: async () => {
      const client = createOperationClient("currentUser");
      const data = await client.request(currentUserQueryDocument);
      return data.currentUser;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const currentUser = useMemo(() => {
    return queryResult.data ?? null;
  }, [queryResult.data]);

  const isSuperUser = useMemo(() => {
    if (!currentUser) {
      return false;
    }
    return !!currentUser.superUser;
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    posthogService.identify(currentUser.id, { email: currentUser.email, name: currentUser.fullname });
    identifyFaroUser(currentUser.id);
  }, [currentUser]);

  if (queryResult.isLoading) {
    return <Loading />;
  }

  if (queryResult.error) {
    console.error(queryResult.error);
    return <ErrorView />;
  }

  if (!currentUser) {
    return <ErrorView />;
  }

  return <CurrentUserContext.Provider value={{ currentUser, isSuperUser }}>{children}</CurrentUserContext.Provider>;
};

export const useCurrentUserContext = (): CurrentUserContext => {
  const context = useContext(CurrentUserContext);

  if (!context) {
    throw new Error("useCurrentUserContext must be used within a CurrentUserContextProvider");
  }

  return context;
};

/**
 *
 * **Important:** Generally, you should not use this hook, but the `useCurrentUserContext` hook.
 *
 * This returns a nullable value, so you need to handle the case where the context is not available.
 *
 */
export const useUnsafeCurrentUserContext = (): CurrentUserContext | null => {
  return useContext(CurrentUserContext);
};

/**
 * Todo need to verify all the scenarios this is needed in.
 */
export const PublicCurrentUserContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const guestUser: CurrentUser = {
    id: "public",
    email: "public@embed",
    fullname: null,
    username: null,
    profilePicture: null,
    superUser: false,
    appearance: null,
    workspaceMembershipList: [],
  };

  const value: CurrentUserContext = {
    currentUser: guestUser,
    isSuperUser: false,
  };

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};
